<template>
    <v-window-item value="details">
        <v-card-text>
            <v-row>
                <v-col cols="12">
                    <h2 class="text-h6 mb-2">Asset Information</h2>
                </v-col>
            </v-row>

            <v-row>
                <!-- Basic Information -->
                <v-col cols="12" md="6" class="pa-1">
                    <v-card elevation="0" class="border-sm rounded-md">
                        <v-card-title class="bg-grey-lighten-4 text-subtitle-1 d-flex ga-4 align-center">
                            <v-icon start>mdi-tag</v-icon>
                                Basic Information
                        </v-card-title>
                        <v-card-text>
                        <v-list density="compact">
                            <v-list-item>
                                <v-list-item-title class="font-weight-bold">Asset Code:</v-list-item-title>
                                <v-list-item-subtitle>{{ data.code ?? "-" }}</v-list-item-subtitle>
                            </v-list-item>

                            <v-list-item>
                                <v-list-item-title class="font-weight-bold">Asset Name:</v-list-item-title>
                                <v-list-item-subtitle>{{ data.name ?? "-" }}</v-list-item-subtitle>
                            </v-list-item>

                            <v-list-item>
                                <v-list-item-title class="font-weight-bold">Category:</v-list-item-title>
                                <v-list-item-subtitle>{{ data.asset_category?.name ?? "-" }}</v-list-item-subtitle>
                            </v-list-item>

                            <v-list-item>
                                <v-list-item-title class="font-weight-bold">Brand:</v-list-item-title>
                                <v-list-item-subtitle>{{ data.brand?.name ?? "-" }}</v-list-item-subtitle>
                            </v-list-item>

                            <v-list-item>
                                <v-list-item-title class="font-weight-bold">Supplier:</v-list-item-title>
                                <v-list-item-subtitle>{{ data.supplier?.supplier_name ?? "-" }}</v-list-item-subtitle>
                            </v-list-item>

                            <v-list-item>
                                <v-list-item-title class="font-weight-bold">Created By:</v-list-item-title>
                                <v-list-item-subtitle>{{ data.created_by?.full_name ?? "-" }}</v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                        </v-card-text>
                    </v-card>
                </v-col>

                <!-- Financial Information -->
                <v-col cols="12" md="6" class="pa-1">
                    <v-card elevation="0" class="border-sm rounded-md">
                        <v-card-title class="bg-grey-lighten-4 text-subtitle-1 d-flex ga-4 align-center">
                            <v-icon start>mdi-currency-usd</v-icon>
                            Financial Information
                            </v-card-title>
                        <v-card-text>
                        <v-list density="compact">
                            <v-list-item>
                                <v-list-item-title class="font-weight-bold">Purchase Date:</v-list-item-title>
                                <v-list-item-subtitle>{{ data.purchase_date ?? "-" }}</v-list-item-subtitle>
                            </v-list-item>

                            <v-list-item>
                                <v-list-item-title class="font-weight-bold">Purchase Cost:</v-list-item-title>
                                <v-list-item-subtitle>৳ {{ data.purchase_cost ?? "-" }}</v-list-item-subtitle>
                            </v-list-item>

                            <v-list-item>
                                <v-list-item-title class="font-weight-bold">Book Value (Start):</v-list-item-title>
                                <v-list-item-subtitle>{{ data.book_value_start ?? "-" }}</v-list-item-subtitle>
                            </v-list-item>

                            <v-list-item>
                                <v-list-item-title class="font-weight-bold">Accumulated Depreciation:</v-list-item-title>
                                <v-list-item-subtitle>{{ data.accumulated_depreciation ?? "-" }}</v-list-item-subtitle>
                            </v-list-item>

                            <v-list-item>
                                <v-list-item-title class="font-weight-bold">Salvage Value:</v-list-item-title>
                                <v-list-item-subtitle>{{ data.salvage_value ?? "-" }}</v-list-item-subtitle>
                            </v-list-item>

                            <v-list-item>
                                <v-list-item-title class="font-weight-bold">Quantity:</v-list-item-title>
                                <v-list-item-subtitle>{{ data.quantity ?? "-" }}</v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                        </v-card-text>
                    </v-card>
                </v-col>

                <!-- Location & Depreciation -->
                <v-col cols="12" md="6" class="pa-1">
                    <v-card elevation="0" class="border-sm rounded-md">
                        <v-card-title class="bg-grey-lighten-4 text-subtitle-1 d-flex ga-4 align-center">
                            <v-icon start>mdi-map-marker</v-icon>
                            Location & Depreciation
                        </v-card-title>
                        <v-card-text>
                            <v-list density="compact">
                                <v-list-item>
                                    <v-list-item-title class="font-weight-bold">Location:</v-list-item-title>
                                    <v-list-item-subtitle>{{ data.asset_location?.name ?? "-" }}</v-list-item-subtitle>
                                </v-list-item>

                                <v-list-item>
                                    <v-list-item-title class="font-weight-bold">Depreciation Method:</v-list-item-title>
                                    <v-list-item-subtitle>{{ data.asset_depreciation_method?.name ?? "-" }}</v-list-item-subtitle>
                                </v-list-item>

                                <v-list-item>
                                    <v-list-item-title class="font-weight-bold">Depreciation Start Date:</v-list-item-title>
                                    <v-list-item-subtitle>{{ data.depreciation_start_date ?? "-" }}</v-list-item-subtitle>
                                </v-list-item>

                                <v-list-item>
                                    <v-list-item-title class="font-weight-bold">Useful Life (Years):</v-list-item-title>
                                    <v-list-item-subtitle>{{ data.useful_life ?? "-" }}</v-list-item-subtitle>
                                </v-list-item>
                            </v-list>
                        </v-card-text>
                    </v-card>
                </v-col>

                <!-- Warranty & Remarks -->
                <v-col cols="12" md="6" class="pa-1">
                    <v-card elevation="0" class="border-sm rounded-md">
                        <v-card-title class="bg-grey-lighten-4 text-subtitle-1 d-flex ga-4 align-center">
                            <v-icon start>mdi-shield-check</v-icon>
                            Warranty & Remarks
                            </v-card-title>
                        <v-card-text>
                            <v-list density="compact">
                                <v-list-item>
                                    <v-list-item-title class="font-weight-bold">Warranty (Months):</v-list-item-title>
                                    <v-list-item-subtitle>{{ data.warranty_month ?? "-" }}</v-list-item-subtitle>
                                </v-list-item>
                            </v-list>

                            <v-divider class="my-2"></v-divider>

                            <div>
                                <v-list-item-title class="font-weight-bold mb-1">Remarks:</v-list-item-title>
                                <v-list-item-subtitle>{{ data.remarks ?? '—'}}</v-list-item-subtitle>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>

                <!-- Image -->
                <v-col cols="12" class="pa-1">
                    <v-card elevation="0" class="border-sm rounded-md">
                        <v-card-title class="bg-grey-lighten-4 text-subtitle-1 d-flex ga-4 align-center">
                            <v-icon start>mdi-image</v-icon>
                            Asset Image
                        </v-card-title>
                        <v-card-text class="d-flex justify-center py-4">
                            <v-img
                                :src="previewImage"
                                max-width="250"
                                aspect-ratio="1"
                                class="rounded-lg elevation-1"
                            ></v-img>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </v-card-text>
    </v-window-item>
</template>


<script>
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { ref } from "vue";
import { toast } from "vue3-toastify";
import { fileServerUrl } from "@/store/api";

export default {
    props: {
        data: Object,
    },
    components: {},
    data: () => ({
        defaultImage : '/src/assets/no_preview.jpeg',
        previewImage: null
    }),
    watch: {
        data: {
            handler(newData) {
                this.previewImage = newData.image
                ?fileServerUrl + newData.image
                :this.defaultImage;
            },
        },
    },
    created() {},
    computed: {},
    setup() {},
    methods: {},
    mounted() {},  
};
</script>
